import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, createFileRoute } from '@tanstack/react-router';
import { SalesSearchView } from '~/components/common/sales-search/sales-search-view';
import { SalesList } from '~/components/common/sales/sales-list/sales-list';
import { SalesListDayByDay } from '~/components/common/sales/sales-list/sales-list-day-by-day';
import { SalesListInfinite } from '~/components/common/sales/sales-list/sales-list-infinite';
import { RegionSelect } from '~/components/region-select';
import { Button } from '~/components/ui/button';
import { cn } from '~/components/ui/cn';
import { CalendarIcon } from '~/components/ui/icons/calendar';
import { SearchIcon } from '~/components/ui/icons/search';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { SaleStatus, useSalesCountQuery } from '~/graphql/graphql.gen';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { SaleType } from '~/stores/sale';
import { titleWrapper } from '~/utils/title';
import { MEUSNIDUS_BASE_URL_FROM_HOST } from '~/utils/url';
import { salesQueryParamsSchema } from '~/validators/sales-validator';

export const SALES_SCOPES = [
  'scope.meusnidus.calendar.view',
  'scope.meusnidus.calendar.write',
  'scope.meusnidus.sales.view',
  'scope.meusnidus.sales.write',
];

export const Route = createFileRoute('/_auth/_main-navigation/ventes/')({
  component: withAuthorizationRequired(RouteComponent, {
    onRedirecting: AppLoader,
    requireScope: SALES_SCOPES,
  }),
  validateSearch: (search) => salesQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(
    searchParams.q ? true : false,
  );
  const [tabs] = useState([
    {
      key: SaleType.Ongoing,
      name: t('sales_page.tabs.active'),
      pageTitle: t('sales_page.tab_urls.active'),
      component: <SalesList status={SaleStatus.Ongoing} />,
    },
    {
      key: SaleType.Next,
      name: t('sales_page.tabs.upcoming'),
      pageTitle: t('sales_page.tab_urls.upcoming'),
      component: <SalesListDayByDay status={SaleStatus.Upcoming} />,
    },
    {
      key: SaleType.Ended,
      name: t('sales_page.tabs.over'),
      pageTitle: t('sales_page.tab_urls.over'),
      component: <SalesListInfinite status={SaleStatus.Ended} />,
    },
    {
      key: SaleType.Cancelled,
      name: t('sales_page.tabs.cancelled'),
      pageTitle: t('sales_page.tab_urls.cancelled'),
      component: <SalesListInfinite status={SaleStatus.Cancelled} />,
    },
  ]);

  const currentTab = tabs.find((tab) => searchParams.type === tab.key);

  const { data: dataSalesCount } = useSalesCountQuery({
    storeId: searchParams.region,
  });

  const handleOpenSearch = () => {
    setIsSearchOpen(true);
  };

  const handleTabChange = (value: SaleType) => {
    navigate({
      search: (prev) => ({
        ...prev,
        type: value,
      }),
    });
  };

  return (
    <>
      <title>{titleWrapper(currentTab?.pageTitle ?? '')}</title>
      <section aria-labelledby="primary-heading" className="relative">
        <SalesSearchView isOpen={isSearchOpen} setIsOpen={setIsSearchOpen} />
        <div className="p-6 sm:p-14">
          <header className="flex flex-col items-start justify-between lg:flex-row lg:items-center">
            <div className="flex w-full flex-col items-start lg:flex-row lg:items-center lg:space-x-8">
              <h1
                id="primary-heading"
                className="text-4.25xl font-bold text-gray-900"
              >
                {t('sales_page.title')}
              </h1>
              <RegionSelect
                className="w-full lg:w-40"
                value={searchParams.region}
                onValueChange={(value) =>
                  navigate({
                    search: (prev) => ({ ...prev, region: value }),
                  })
                }
              />
            </div>
            <div className="w-full border-none border-gray-100 sm:hidden lg:border-b">
              <label htmlFor="tab-list" className="sr-only">
                {t('sales_page.filter_by')}
              </label>
              <select
                id="tab-list"
                className="form-input mt-6 block w-full rounded border border-gray-500 p-2 text-sm leading-5.5 text-gray-900 outline-none transition duration-300 placeholder:text-gray-500 hover:border-gray-700 focus:border-gray-700 focus:ring-gray-700"
                value={currentTab?.key}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleTabChange(e.target.value as SaleType)
                }
              >
                {tabs.map((tab) => (
                  <option key={tab.key} value={tab.key}>
                    {tab.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-6 flex shrink-0 items-center space-x-4 lg:mt-0">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleOpenSearch}
              >
                <SearchIcon className="size-6" />
              </Button>
              <Button variant="outline" size="icon" asChild>
                <Link
                  to="/calendar"
                  search={{
                    category: [],
                    csm: [],
                    division: [],
                    operationType: [],
                    plugin: [],
                    region: searchParams.region,
                    sales: [],
                    setup: [],
                    tiering: [],
                    topBrandType: [],
                    type: [],
                  }}
                >
                  <CalendarIcon className="size-6" />
                </Link>
              </Button>
              <Button asChild>
                <a href={`${MEUSNIDUS_BASE_URL_FROM_HOST}/creation/vente/`}>
                  {t('sales_page.create_page')}
                </a>
              </Button>
            </div>
          </header>

          <Tabs
            value={currentTab?.key}
            onValueChange={(value) => handleTabChange(value as SaleType)}
          >
            <TabsList className="hidden shrink-0 space-x-10 pt-5 sm:flex">
              {tabs.map((tab, index) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  className="group items-end space-x-2"
                >
                  <>
                    <div>{tab.name}</div>
                    <div
                      className={cn(
                        'text-base font-bold leading-5.5 text-gray-300 group-data-[state=active]:text-green-400',
                        {
                          hidden: index >= 2,
                        },
                      )}
                    >
                      (
                      {dataSalesCount?.salesCount
                        ? dataSalesCount.salesCount[index]?.count
                        : ''}
                      )
                    </div>
                  </>
                </TabsTrigger>
              ))}
            </TabsList>

            {tabs.map((tab) => (
              <TabsContent key={tab.key} value={tab.key} className="mt-8">
                {tab.component}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </>
  );
}
